import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { IonIcon, IonSpinner, IonToast } from '@ionic/react';
import {
  searchOutline,
  linkOutline,
  sendOutline,
  imageOutline,
  copyOutline,
  arrowBackOutline,
  settingsOutline,
  sparklesOutline,
  personOutline,
  refreshOutline,
  peopleOutline,
  phonePortraitOutline,
  flagOutline,
  documentTextOutline,
  hourglassOutline,
  alertCircleOutline,
  swapHorizontalOutline,
  ticketOutline,
  timeOutline,
} from 'ionicons/icons';

// Readable delivery-status chip for outbound messages (intentionally clearer than WhatsApp's tiny ticks).
const OutStatus: React.FC<{ status?: string }> = ({ status }) => {
  const base = 'px-1.5 py-0.5 rounded-full text-[11px] font-semibold leading-none';
  if (status === 'failed')
    return <span className={base + ' bg-red-500 text-white'}>Failed</span>;
  if (status === 'read')
    return <span className={base + ' bg-sky-400 text-white'}>✓✓ Read</span>;
  if (status === 'delivered')
    return <span className={base + ' bg-white/40 text-white'}>✓✓ Delivered</span>;
  if (status === 'sent')
    return <span className={base + ' bg-white/25 text-white'}>✓ Sent</span>;
  return null;
};
import {
  listConversations,
  getThread,
  replyToConversation,
  sendPhotoReply,
  requestAttendeeAges,
  askHasIphone,
  askSupporterClub,
  sendOfferFollowUp,
  askHasIphoneBulk,
  sendTicketsComingBulk,
  offerSeats,
  deliverTickets,
  releasePendingDelivery,
  uploadInstructionsPdf,
  setAiMuted,
  setAwaitingOfferResponse,
  setChatIssue,
  getWaSettings,
  updateWaSettings,
  backfill,
  sendTestTemplate,
  resetSent,
  getConversationOrders,
  scanAges,
  WaConversationSummary,
  WaThread,
  WaSettings,
  BackfillResult,
  BulkIphoneResult,
  OrderDetail,
  AgesScanReport,
  fetchMediaObjectUrl,
  searchSentLinks,
  SentLinkHit,
} from '../services/whatsappService';

// The three seat-offer templates. All three ASK — none of them tells the buyer a change has already
// happened. Nothing moves unless they say yes, and the wording says so explicitly.
//
// The third carries the split as well, because a block change usually moves the split with it
// ("up to 2 together now, in this block I can get all 4 of you together") and that's the part that
// sells it. There's no split-only template — a split never changes on its own.
type OfferTemplate = 'seat_upgrade_offer' | 'seat_category_offer' | 'seat_category_split_offer';

const OFFER_TEMPLATES: {
  v: OfferTemplate;
  label: string;
  hint: string;
  currentLabel: string;
  offerLabel: string;
  offerPh: string;
  splits?: boolean; // shows the current/offered split fields (6-variable template)
}[] = [
  {
    v: 'seat_upgrade_offer',
    label: 'Upgrade',
    hint: 'Better category or block',
    currentLabel: 'Currently booked',
    offerLabel: 'Moving them to',
    offerPh: 'Club Level block 5, 3 together',
  },
  {
    v: 'seat_category_offer',
    label: 'Category',
    hint: 'Different area, sold as better',
    currentLabel: 'Currently booked',
    offerLabel: 'Can offer them',
    offerPh: 'Longside Lower block 12',
  },
  {
    v: 'seat_category_split_offer',
    label: 'Category + split',
    hint: 'New block, and they sit differently',
    currentLabel: 'Currently booked',
    offerLabel: 'Can offer them',
    offerPh: 'Longside Lower block 12',
    splits: true,
  },
];

// Platforms that carry buyer phone numbers (FTN anonymizes → excluded).
const PLATFORM_OPTIONS = [
  { value: 'HelloTickets', label: 'HelloTickets' },
  { value: 'LiveFootballTickets', label: 'Live' },
  { value: 'StubHub', label: 'StubHub' },
  { value: 'Vivid 1', label: 'Vivid 1' },
  { value: 'Vivid 2', label: 'Vivid 2' },
];

// Short internal platform label for the conversation identifier.
const shortPlatform = (p?: string) => {
  const s = (p || '').toLowerCase();
  if (s.includes('hello')) return 'Hello';
  if (s.includes('livefootball')) return 'Live';
  if (s.includes('stub')) return 'StubHub';
  if (s.includes('vivid')) return 'Vivid';
  if (s.includes('ftn')) return 'FTN';
  return p || '';
};

// Avatar initials: two letters from the buyer name (first + last word), else first two of the name,
// else the last two digits of the phone.
const avatarInitials = (name?: string, phone?: string): string => {
  const n = (name || '').trim();
  if (n) {
    const parts = n.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return n.slice(0, 2).toUpperCase();
  }
  const d = (phone || '').replace(/\D/g, '');
  return d ? d.slice(-2) : '#';
};

// Stable colour from a seed (the phone, so the same number always gets the same colour to scan for).
const avatarColor = (seed: string): string => {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360} 52% 45%)`;
};

const Avatar = ({ name, phone, size = 34 }: { name?: string; phone?: string; size?: number }) => (
  <span
    className="shrink-0 rounded-full flex items-center justify-center text-white font-semibold select-none"
    style={{
      width: size,
      height: size,
      fontSize: Math.round(size * 0.4),
      background: avatarColor((phone || name || '?').toLowerCase()),
    }}
  >
    {avatarInitials(name, phone)}
  </span>
);

// One label/value row in the order-details panel; renders nothing when the value is empty.
const Field = ({ label, value }: { label: string; value: React.ReactNode }) =>
  value !== null && value !== undefined && value !== '' ? (
    <div>
      <span className="text-gray-500 dark:text-gray-400">{label}:</span>{' '}
      <span className="font-medium">{value}</span>
    </div>
  ) : null;

// Primary label for a conversation: "<order ids> - <platform>" (e.g. "148394832 - Live", or
// "12345 - 88888 - Live" when the same buyer has several orders). Uses the order id for every
// platform (incl. Hello — the Hello reference is only used in the template). Falls back to buyer
// name / phone when there's no linked order (inbound-only chat).
const convLabel = (c: {
  orderId?: string;
  orderIds?: string[];
  platform?: string;
  buyerName?: string;
  phone: string;
}) => {
  const ids = c.orderIds && c.orderIds.length ? c.orderIds : c.orderId ? [c.orderId] : [];
  if (ids.length) return `${ids.join(' - ')} - ${shortPlatform(c.platform)}`;
  return c.buyerName || c.phone;
};

// Inbound attachment. The bytes come through the authed API, so an <img src> to the endpoint
// would 401 — fetch as a blob and render the object URL, revoking it on unmount.
const Attachment: React.FC<{ wamid?: string; type: string; mimeType?: string; filename?: string }> = ({ wamid, type, mimeType, filename }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (!wamid) return;
    let dead = false;
    let made: string | null = null;
    fetchMediaObjectUrl(wamid)
      .then((u) => { if (dead) { URL.revokeObjectURL(u); return; } made = u; setUrl(u); })
      .catch(() => !dead && setErr(true));
    return () => { dead = true; if (made) URL.revokeObjectURL(made); };
  }, [wamid]);
  if (!wamid) return <span className="opacity-70">[{type}]</span>;
  if (err) return <span className="opacity-70">[{type} — could not load]</span>;
  if (!url) return <span className="opacity-60">loading {type}…</span>;
  const isImage = type === 'image' || type === 'sticker' || (mimeType || '').startsWith('image/');
  if (isImage)
    return (
      <a href={url} target="_blank" rel="noreferrer">
        <img src={url} alt={filename || type} className="rounded-lg max-h-64 max-w-full object-contain" />
      </a>
    );
  if ((mimeType || '').startsWith('audio/')) return <audio controls src={url} className="max-w-full" />;
  if ((mimeType || '').startsWith('video/')) return <video controls src={url} className="rounded-lg max-h-64 max-w-full" />;
  return (
    <a href={url} download={filename || 'attachment'} className="underline break-all">
      {filename || `Download ${type}`}
    </a>
  );
};

type ComposerHandle = { setText: (t: string) => void; focus: () => void };

// The reply box owns its draft. It used to live in MessagesContent's state, so every keystroke
// re-rendered the entire inbox — conversation list, thread, panels — and typing lagged visibly on
// long threads. Now a keystroke re-renders this component and nothing else; the parent only hears
// about a finished message.
const ReplyComposer = forwardRef<
  ComposerHandle,
  {
    chatPhone: string;
    sendingPhoto: boolean;
    onSend: (text: string) => void;
    onSendPhoto: (file: File, caption: string) => void;
  }
>(function ReplyComposer({ chatPhone, sendingPhoto, onSend, onSendPhoto }, ref) {
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const photoRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({ setText: setDraft, focus: () => inputRef.current?.focus() }), []);

  // When a chat opens, put the cursor in the reply box so you can type immediately. Keyed on the
  // phone so the 15s poll doesn't keep stealing focus.
  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [chatPhone]);

  // Auto-grow the reply box as text wraps to more lines (capped so it can't swallow the whole panel),
  // and shrink it back to one line once the draft is cleared (e.g. right after sending).
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  }, [draft]);

  const submit = () => {
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    onSend(text);
  };

  return (
    <div className="flex items-end gap-2 p-2 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
      <input
        ref={photoRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = ''; // allow re-picking the same file
          if (f) {
            const caption = draft.trim();
            setDraft('');
            onSendPhoto(f, caption);
          }
        }}
      />
      <button
        onClick={() => photoRef.current?.click()}
        disabled={sendingPhoto}
        title="Send a photo (any text in the box goes with it as the caption)"
        className="p-2.5 rounded-full border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 disabled:opacity-40 shrink-0"
      >
        {sendingPhoto ? <IonSpinner name="crescent" style={{ width: 16, height: 16 }} /> : <IonIcon icon={imageOutline} />}
      </button>
      <textarea
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
        placeholder="Type a reply… (Shift+Enter for a new line)"
        rows={1}
        className="flex-1 px-3 py-2 rounded-2xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm resize-none leading-snug max-h-[120px] overflow-y-auto"
      />
      <button onClick={submit} disabled={!draft.trim()} className="p-2.5 rounded-full bg-green-600 text-white disabled:opacity-40 shrink-0">
        <IonIcon icon={sendOutline} />
      </button>
    </div>
  );
});

const fmtTime = (s?: string) =>
  s ? new Date(s).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

type OfferForm = {
  template: OfferTemplate;
  event: string;
  current: string;
  offer: string;
  currentSplit: string;
  offerSplit: string;
  promo: boolean;
};

const EMPTY_OFFER: OfferForm = {
  template: 'seat_upgrade_offer',
  event: '',
  current: '',
  offer: '',
  currentSplit: '',
  offerSplit: '',
  promo: false,
};

// Seat-offer composer. Two approved templates behind one form — pick the framing, fill in what
// they're on now and what you can move them to. It owns the form text: keeping that state inside
// the (very large) Messages screen made every keystroke re-render the conversation list and the
// whole thread, which is why typing here used to lag.
const OfferComposer: React.FC<{
  initial: OfferForm;
  sending: boolean;
  onCancel: () => void;
  onSend: (form: OfferForm) => void;
}> = React.memo(({ initial, sending, onCancel, onSend }) => {
  const [form, setForm] = useState<OfferForm>(initial);
  const set = (k: keyof OfferForm, v: string | boolean) => setForm((p) => ({ ...p, [k]: v }));

  const active = OFFER_TEMPLATES.find((t) => t.v === form.template) || OFFER_TEMPLATES[0];

  // WhatsApp rejects a template with any blank variable, so every field the chosen template uses
  // has to be filled before Send is live.
  const ready = (
    ['event', 'current', 'offer', ...(active.splits ? ['currentSplit', 'offerSplit'] : [])] as (keyof OfferForm)[]
  ).every((k) => String(form[k] || '').trim());

  const fields = (
    [
      { k: 'event', label: 'Match', ph: 'Arsenal vs Chelsea', on: true },
      { k: 'current', label: active.currentLabel, ph: 'Longside Lower x4', on: true },
      { k: 'currentSplit', label: 'Their split now', ph: 'Up To 2 Together', on: !!active.splits },
      { k: 'offer', label: active.offerLabel, ph: active.offerPh, on: true },
      { k: 'offerSplit', label: 'Split on the offer', ph: 'All 4 together', on: !!active.splits },
    ] as { k: keyof OfferForm; label: string; ph: string; on: boolean }[]
  ).filter((f) => f.on);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onCancel}>
      <div
        className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-900 shadow-xl p-4 space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="font-semibold text-gray-900 dark:text-gray-100">Offer different seats</div>

        <div className="grid grid-cols-3 gap-2">
          {OFFER_TEMPLATES.map((t) => (
            <button
              key={t.v}
              onClick={() => set('template', t.v)}
              className={
                'text-left px-2.5 py-2 rounded-lg border text-xs ' +
                (form.template === t.v
                  ? 'bg-emerald-600 border-emerald-600 text-white'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300')
              }
            >
              <div className="font-semibold">{t.label}</div>
              <div className="opacity-80 leading-tight">{t.hint}</div>
            </button>
          ))}
        </div>

        {fields.map((f) => (
          <label key={f.k} className="block text-xs">
            <span className="text-gray-500 dark:text-gray-400">{f.label}</span>
            <input
              value={String(form[f.k])}
              onChange={(e) => set(f.k, e.target.value)}
              placeholder={f.ph}
              className="mt-0.5 w-full px-2 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-950 text-sm text-gray-900 dark:text-gray-100"
            />
          </label>
        ))}

        {/* Utility by default: cheaper per message, and reaches buyers who've opted out of
            marketing. The promo variant is the same offer sold hard — opt in per send. */}
        <label className="flex items-start gap-2 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={form.promo}
            onChange={(e) => set('promo', e.target.checked)}
            className="mt-0.5"
          />
          <span className="text-gray-600 dark:text-gray-300">
            Sell it harder
            <span className="text-gray-500 dark:text-gray-400">
              {' '}
              — excited wording, costs more per message, and won't reach buyers who opted out of
              marketing
            </span>
          </span>
        </label>

        <p className="text-[11px] text-gray-500 dark:text-gray-400">
          Sends as an approved template, so it reaches them even outside the 24-hour window. Their
          button tap re-opens the window — you can then reply in free text. The chat is flagged
          “Awaiting reply” automatically.
        </p>

        <div className="flex gap-2 justify-end pt-1">
          <button
            onClick={onCancel}
            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={() => onSend(form)}
            disabled={sending || !ready}
            className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium disabled:opacity-50"
          >
            {sending ? 'Sending…' : 'Send offer'}
          </button>
        </div>
      </div>
    </div>
  );
});


const MessagesContent: React.FC = () => {
  const [convos, setConvos] = useState<WaConversationSummary[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [thread, setThread] = useState<WaThread | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'sent' | 'delivered' | 'read' | 'failed'>('all');
  const [awaitingOnly, setAwaitingOnly] = useState(false);
  // Tickets uploaded and held, waiting on the buyer to confirm they've read the instructions.
  const [awaitingTicketsOnly, setAwaitingTicketsOnly] = useState(false);
  // Chats manually flagged as a problem.
  const [issueOnly, setIssueOnly] = useState(false);
  // "Find a ticket link" — searches the permanent sent-link record, separate from the chat search.
  const [linkModal, setLinkModal] = useState(false);
  const [linkQuery, setLinkQuery] = useState('');
  const [linkHits, setLinkHits] = useState<SentLinkHit[] | null>(null);
  const [linkSearching, setLinkSearching] = useState(false);
  const doLinkSearch = async () => {
    if (linkQuery.trim().length < 4) { setToast('Enter at least 4 characters of the link'); return; }
    setLinkSearching(true);
    try { setLinkHits(await searchSentLinks(linkQuery.trim())); }
    catch { setToast('Link search failed'); }
    finally { setLinkSearching(false); }
  };
  const [toast, setToast] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState<WaSettings | null>(null);
  const [askingAges, setAskingAges] = useState(false);
  const [askingIphone, setAskingIphone] = useState(false);
  const [askingClub, setAskingClub] = useState(false);
  const [chasingOffer, setChasingOffer] = useState(false);
  // Bulk "does anyone have an iPhone?" — paste a list of order ids and send to all their buyers.
  const [bulkIphoneOpen, setBulkIphoneOpen] = useState(false);
  const [bulkIphoneIds, setBulkIphoneIds] = useState('');
  const [bulkIphoneSending, setBulkIphoneSending] = useState(false);
  const [bulkIphoneResult, setBulkIphoneResult] = useState<BulkIphoneResult | null>(null);
  // "tickets are on the way" bulk sender — same shape as the iPhone one.
  const [bulkComingOpen, setBulkComingOpen] = useState(false);
  const [bulkComingIds, setBulkComingIds] = useState('');
  const [bulkComingSending, setBulkComingSending] = useState(false);
  const [bulkComingResult, setBulkComingResult] = useState<BulkIphoneResult | null>(null);
  // Order details are cached per phone (loaded once per chat, kept until an explicit refresh) and the
  // panel is open by default; `orderMinimized` is a global collapse preference across chats.
  const [ordersByPhone, setOrdersByPhone] = useState<Record<string, OrderDetail[]>>({});
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [orderMinimized, setOrderMinimized] = useState(false);
  const ordersLoadedRef = useRef<Set<string>>(new Set());
  const bottomRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<ComposerHandle>(null);
  const [sendingPhoto, setSendingPhoto] = useState(false);

  const loadList = async () => {
    try {
      setConvos(await listConversations());
    } catch (e: any) {
      setToast(e?.response?.data?.message || e?.message || 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  // The chat currently open, readable from inside an in-flight request. Threads are refetched on a
  // 15s poll and after every action, so there are nearly always requests in the air for the chat
  // you just LEFT; applying whichever answered last put the old thread back on screen and made a
  // click on the list look like it had done nothing. A response only lands if it is still for the
  // selected chat.
  const selectedRef = useRef<string | null>(null);
  selectedRef.current = selected;

  const loadThread = async (phone: string) => {
    try {
      const t = await getThread(phone);
      if (selectedRef.current === phone) setThread(t);
    } catch (e: any) {
      if (selectedRef.current === phone) setToast(e?.message || 'Failed to load thread');
    }
  };

  // Orders screen hands off here with ?deliver=<digits> (open that buyer's chat AND the delivery
  // dialog) or ?chat=<digits> (just open the chat). Params are stripped after handling so refresh
  // doesn't re-trigger them.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const deliver = (params.get('deliver') || '').replace(/\D/g, '');
    const chat = (params.get('chat') || '').replace(/\D/g, '');
    const phone = deliver || chat;
    if (phone) {
      setSelected(phone);
      loadThread(phone);
      if (deliver) setDeliverOpen(true);
      params.delete('deliver');
      params.delete('chat');
      window.history.replaceState({}, '', window.location.pathname + (params.toString() ? `?${params}` : ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadList();
    getWaSettings().then(setSettings).catch(() => undefined);
  }, []);

  // Light polling so new messages appear without a manual refresh.
  useEffect(() => {
    const id = setInterval(() => {
      loadList();
      if (selected) loadThread(selected);
    }, 15000);
    return () => clearInterval(id);
  }, [selected]);

  // Switching chats clears the pane at once, so the new one loads into an empty pane (with a
  // spinner) rather than over the top of the old thread.
  useEffect(() => {
    setThread(null);
    if (selected) loadThread(selected);
  }, [selected]);

  // Auto-load the order details for a chat once (cached until an explicit refresh); panel opens by default.
  useEffect(() => {
    const p = selected;
    if (!p || ordersLoadedRef.current.has(p)) return;
    ordersLoadedRef.current.add(p);
    setLoadingOrders(true);
    getConversationOrders(p)
      .then((r) => setOrdersByPhone((m) => ({ ...m, [p]: r.orders || [] })))
      .catch(() => {
        ordersLoadedRef.current.delete(p); // allow a retry on next entry
        setToast('Could not load order details');
      })
      .finally(() => setLoadingOrders(false));
  }, [selected]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thread?.messages?.length]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = convos;
    if (statusFilter !== 'all') list = list.filter((c) => c.lastOutStatus === statusFilter);
    if (awaitingOnly) list = list.filter((c) => c.awaitingOfferResponse);
    if (awaitingTicketsOnly) list = list.filter((c) => c.deliveryState === 'awaiting');
    if (issueOnly) list = list.filter((c) => c.isIssue);
    if (q) {
      list = list.filter(
        (c) =>
          c.buyerName?.toLowerCase().includes(q) ||
          c.phone.includes(q) ||
          c.eventName?.toLowerCase().includes(q) ||
          c.orderId?.toLowerCase().includes(q) ||
          (c.orderIds || []).some((id) => id.toLowerCase().includes(q)),
      );
    }
    return list;
  }, [convos, search, statusFilter, awaitingOnly, awaitingTicketsOnly, issueOnly]);

  // Counts per delivery status (of the most recent outbound message) for the filter chips.
  const statusCounts = useMemo(() => {
    const c = { all: convos.length, sent: 0, delivered: 0, read: 0, failed: 0 };
    for (const conv of convos) {
      if (conv.lastOutStatus && conv.lastOutStatus in c) (c as any)[conv.lastOutStatus]++;
    }
    return c;
  }, [convos]);

  const awaitingCount = useMemo(() => convos.filter((c) => c.awaitingOfferResponse).length, [convos]);
  const issueCount = useMemo(() => convos.filter((c) => c.isIssue).length, [convos]);
  const awaitingTicketsCount = useMemo(
    () => convos.filter((c) => c.deliveryState === 'awaiting').length,
    [convos],
  );

  const send = (raw: string) => {
    const text = raw.trim();
    if (!selected || !text) return;
    const phone = selected;
    // The composer has already cleared its box; show the message optimistically so you can type the
    // next reply right away without waiting for the network round-trip.
    const optimistic = {
      direction: 'out' as const,
      type: 'text',
      text,
      status: 'sent',
      at: new Date().toISOString(),
    };
    setThread((t) =>
      t && t.phone === phone ? { ...t, messages: [...t.messages, optimistic] } : t,
    );
    // Fire-and-forget; reconcile with the server afterwards (real status/wamid, or a 'failed' chip).
    (async () => {
      try {
        await replyToConversation(phone, text);
      } catch (e: any) {
        setToast(e?.response?.data?.message || 'Failed to send');
      } finally {
        loadThread(phone);
        loadList();
      }
    })();
  };

  // Send a photo as a manual reply. Whatever's typed in the box rides along as the caption (and the
  // box clears). No optimistic bubble — the real message (with the rendered image) arrives on the
  // thread reload after the upload completes.
  const sendPhoto = (file: File, caption: string) => {
    if (!selected || sendingPhoto) return;
    const phone = selected;
    setSendingPhoto(true);
    (async () => {
      try {
        await sendPhotoReply(phone, file, caption || undefined);
      } catch (e: any) {
        setToast(e?.response?.data?.message || 'Failed to send photo');
        if (caption) composerRef.current?.setText(caption); // give the caption back on failure
      } finally {
        setSendingPhoto(false);
        loadThread(phone);
        loadList();
      }
    })();
  };

  const copyPhone = async (phone: string) => {
    const value = '+' + String(phone || '').replace(/\D/g, '');
    try {
      await navigator.clipboard.writeText(value);
      setToast(`Copied ${value}`);
    } catch {
      // Older mobile browsers without the async clipboard API
      const ta = document.createElement('textarea');
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setToast(`Copied ${value}`);
    }
  };

  const askAges = async () => {
    if (!selected) return;
    setAskingAges(true);
    try {
      await requestAttendeeAges(selected);
      await loadThread(selected);
      loadList();
      setToast('Attendee-ages request sent');
    } catch (e: any) {
      setToast(e?.response?.data?.message || 'Failed to send ages request');
    } finally {
      setAskingAges(false);
    }
  };

  const askIphone = async () => {
    if (!selected) return;
    setAskingIphone(true);
    try {
      await askHasIphone(selected);
      await loadThread(selected);
      loadList();
      setToast('“Anyone have an iPhone?” sent');
    } catch (e: any) {
      setToast(e?.response?.data?.message || 'Failed to send iPhone question');
    } finally {
      setAskingIphone(false);
    }
  };

  const askClub = async () => {
    if (!selected) return;
    setAskingClub(true);
    try {
      const r = await askSupporterClub(selected);
      await loadThread(selected);
      loadList();
      setToast(`“Which club do you support?” sent (${(r?.teams || []).join(' / ')} / Neutral)`);
    } catch (e: any) {
      setToast(e?.response?.data?.message || 'Failed to send club question');
    } finally {
      setAskingClub(false);
    }
  };

  const chaseOffer = async () => {
    if (!selected) return;
    setChasingOffer(true);
    try {
      await sendOfferFollowUp(selected);
      await loadThread(selected);
      loadList();
      setToast('Offer follow-up sent');
    } catch (e: any) {
      setToast(e?.response?.data?.message || 'Failed to send offer follow-up');
    } finally {
      setChasingOffer(false);
    }
  };

  // Parse ids from the pasted blob: split on anything that isn't part of an id (newlines, commas,
  // spaces), keep non-empty tokens, dedupe.
  const parsedBulkIds = Array.from(
    new Set(bulkIphoneIds.split(/[^A-Za-z0-9_-]+/).map((s) => s.trim()).filter(Boolean)),
  );

  const parsedComingIds = Array.from(
    new Set(bulkComingIds.split(/[^A-Za-z0-9_-]+/).map((s) => s.trim()).filter(Boolean)),
  );

  const runBulkComing = async () => {
    if (!parsedComingIds.length) return;
    setBulkComingSending(true);
    setBulkComingResult(null);
    try {
      const res = await sendTicketsComingBulk(parsedComingIds);
      setBulkComingResult(res);
      setToast(`Tickets-on-the-way: ${res.sent} sent, ${res.failed} failed`);
      loadList();
    } catch (e: any) {
      setToast(e?.response?.data?.message || 'Bulk send failed');
    } finally {
      setBulkComingSending(false);
    }
  };

  const runBulkIphone = async () => {
    if (!parsedBulkIds.length) return;
    setBulkIphoneSending(true);
    setBulkIphoneResult(null);
    try {
      const res = await askHasIphoneBulk(parsedBulkIds);
      setBulkIphoneResult(res);
      setToast(`iPhone question: ${res.sent} sent, ${res.failed} failed`);
      loadList();
    } catch (e: any) {
      setToast(e?.response?.data?.message || 'Bulk send failed');
    } finally {
      setBulkIphoneSending(false);
    }
  };

  // ── Seat-offer composer ────────────────────────────────────────────────────
  // Prefilled from the linked order (match + current category) so the common case is: click Offer,
  // type what you can give them, send.
  const [offerOpen, setOfferOpen] = useState(false);
  const [offerSending, setOfferSending] = useState(false);
  // Only the prefill lives here; the text being typed is local to OfferComposer.
  const [offerInitial, setOfferInitial] = useState<OfferForm>(EMPTY_OFFER);

  const firstOrder = (): OrderDetail | undefined =>
    selected ? (ordersByPhone[selected] || [])[0] : undefined;

  // Prefills from the linked order: category + quantity for where they sit, and the order's remarks
  // for the split — that's where "Up To 2 Together" is stored.
  const currentFor = (o?: OrderDetail): string =>
    o ? [o.category, o.quantity ? `x${o.quantity}` : ''].filter(Boolean).join(' ') : '';
  const currentSplitFor = (o?: OrderDetail): string => String(o?.remarks || '');

  const openOfferComposer = () => {
    const o = firstOrder();
    setOfferInitial({
      template: 'seat_upgrade_offer',
      event: String(o?.event || ''),
      current: currentFor(o),
      offer: '',
      currentSplit: currentSplitFor(o),
      offerSplit: '',
      promo: false,
    });
    setOfferOpen(true);
  };

  // ── Ticket delivery ────────────────────────────────────────────────────────
  const [deliverOpen, setDeliverOpen] = useState(false);
  const [deliverSending, setDeliverSending] = useState(false);
  const [deliverLinks, setDeliverLinks] = useState('');
  const [deliverFiles, setDeliverFiles] = useState<File[]>([]);
  // One-time links: buyer gets the lock warning right before the links (WhatsApp + email alike).
  const [deliverOneTime, setDeliverOneTime] = useState(false);
  const [deliverReplacement, setDeliverReplacement] = useState(false);
  const [deliverOrderId, setDeliverOrderId] = useState('');
  // Every order on this thread. A thread is per phone, so a repeat buyer has several; the delivery
  // has to name one, because proof and platform completion are filed against that order only.
  const threadOrderIds: string[] = thread?.orderIds?.length ? thread.orderIds : thread?.orderId ? [thread.orderId] : [];
  // Same ticket, two wallets: an Apple link + Google link pair the buyer sees as ONE labelled
  // ticket. One row per ticket; either half may be blank when only one platform link exists.
  const [deliverPairs, setDeliverPairs] = useState<{ iphone: string; android: string }[]>([]);
  // URLs extracted by pattern, not by line — a long link WRAPS visually without containing a
  // newline, and a paste with two links on one line must still count as two. This list is what
  // actually gets sent, and the count under the box shows exactly what was recognised.
  const parsedDeliverLinks = useMemo(
    () => (deliverLinks.match(/https?:\/\/\S+/g) || []).map((l) => l.trim()),
    [deliverLinks],
  );

  const submitDelivery = async (mode: 'gate' | 'now' | 'both') => {
    if (!selected) return;
    setDeliverSending(true);
    try {
      const filledPairs = deliverPairs.filter((p) => p.iphone.trim() || p.android.trim());
      const orderId = deliverOrderId || (threadOrderIds.length === 1 ? threadOrderIds[0] : '');
      if (threadOrderIds.length > 1 && !orderId) { setToast('Pick which order these tickets are for'); return; }
      const res = await deliverTickets(selected, { links: parsedDeliverLinks.join('\n'), linkPairs: filledPairs, files: deliverFiles, mode, oneTimeLinks: deliverOneTime, replacement: deliverReplacement, orderId });
      setDeliverOpen(false);
      setDeliverLinks('');
      setDeliverFiles([]);
      setDeliverPairs([]);
      setDeliverOneTime(false);
      setDeliverReplacement(false);
      setDeliverOrderId('');
      await loadThread(selected);
      loadList();
      setToast(
        mode === 'gate'
          ? `Instructions sent — holding ${res?.held?.documents ?? 0} PDF(s) + ${res?.held?.links ?? 0} link(s) until they confirm`
          : mode === 'both'
          ? 'Instructions and tickets both sent'
          : 'Tickets sent',
      );
    } catch (e: any) {
      setToast(e?.response?.data?.message || 'Delivery failed');
    } finally {
      setDeliverSending(false);
    }
  };

  const sendHeldNow = async () => {
    if (!selected) return;
    try {
      await releasePendingDelivery(selected);
      await loadThread(selected);
      setToast('Held tickets sent');
    } catch (e: any) {
      setToast(e?.response?.data?.message || 'Send failed');
    }
  };

  const sendOffer = async (form: OfferForm) => {
    if (!selected) return;
    setOfferSending(true);
    try {
      await offerSeats(selected, form);
      setOfferOpen(false);
      await loadThread(selected);
      loadList();
      setToast('Offer sent — chat marked as awaiting reply');
    } catch (e: any) {
      setToast(e?.response?.data?.message || 'Failed to send the offer');
    } finally {
      setOfferSending(false);
    }
  };

  // Explicit refresh (the top-bar button): reload the list AND drop the order cache so details refetch.
  const refreshAll = () => {
    setOrdersByPhone({});
    ordersLoadedRef.current.clear();
    loadList();
  };

  const toggleMute = async () => {
    if (!selected || !thread) return;
    try {
      const updated = await setAiMuted(selected, !thread.aiMuted);
      setThread(updated);
      loadList();
    } catch {
      setToast('Failed to update AI takeover');
    }
  };

  const toggleAwaitingOffer = async () => {
    if (!selected || !thread) return;
    try {
      const updated = await setAwaitingOfferResponse(selected, !thread.awaitingOfferResponse);
      setThread(updated);
      loadList();
    } catch {
      setToast('Failed to update awaiting-offer flag');
    }
  };

  const toggleIssue = async () => {
    if (!selected || !thread) return;
    try {
      const updated = await setChatIssue(selected, !thread.isIssue);
      setThread(updated);
      loadList();
    } catch {
      setToast('Failed to update issue flag');
    }
  };

  const saveSettings = async (patch: Partial<WaSettings>) => {
    try {
      setSettings(await updateWaSettings(patch));
    } catch {
      setToast('Failed to save settings');
    }
  };

  // One-off: message existing buyers on the ticked platform(s) (upcoming matches) with confirmation + ages.
  // Always dry-runs first and shows the real counts in a confirm before sending anything.
  // Pass limit=3 for a small test send.
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState<BackfillResult | null>(null);
  const [backfillPlatforms, setBackfillPlatforms] = useState<string[]>([]);
  const runBackfillLive = async (testLimit?: number) => {
    if (backfilling) return;
    if (!backfillPlatforms.length) {
      setToast('Tick at least one platform to backfill');
      return;
    }
    setBackfilling(true);
    try {
      const dry = await backfill(true, backfillPlatforms);
      if ('error' in dry && dry.error) {
        setToast(dry.error);
        return;
      }
      if (dry.dryRun) {
        const plat = backfillPlatforms.join(', ');
        if (dry.eligible === 0) {
          setToast(
            `No eligible orders for ${plat}. (${dry.totalLiveUnsentOrders} un-messaged total; ` +
              `${dry.skippedNoPhone} have no phone, ${dry.skippedNoEventDate} have no match date.)`,
          );
          return;
        }
        const n = testLimit ? Math.min(testLimit, dry.eligible) : Math.min(dry.perRunCap, dry.eligible);
        const ok = window.confirm(
          (testLimit
            ? `TEST: send the confirmation to just the first ${n} of ${dry.eligible} eligible ${plat} buyer(s). Check they look right, then run the full batch.\n\n`
            : `Send the confirmation to ${n} ${plat} buyer(s) for upcoming matches` +
              `${dry.alsoGetAges ? ` (${dry.alsoGetAges} also get the ages request)` : ''}.\n\n` +
              `Skipped: ${dry.skippedNoPhone} no phone, ${dry.skippedNoEventDate} no match date.\n` +
              `Sends up to ${dry.perRunCap} per run (re-run for the rest).\n\n`) +
            `These are real WhatsApp messages and cannot be unsent. Proceed?`,
        );
        if (!ok) return;
      }
      const res = await backfill(false, backfillPlatforms, testLimit);
      if ('error' in res && res.error) {
        setToast(res.error);
      } else if (!res.dryRun) {
        setBackfillResult(res);
        setToast(
          `Sent ${res.sent} confirmation(s)` +
            `${res.agesSent ? `, ${res.agesSent} ages` : ''}` +
            `${res.failed ? `, ${res.failed} failed` : ''}. ${res.note}`,
        );
      }
    } catch {
      setToast('Backfill failed — check server logs.');
    } finally {
      setBackfilling(false);
    }
  };

  // Send both templates to one test number with dummy details.
  const [testNumber, setTestNumber] = useState('');
  const [testSending, setTestSending] = useState(false);
  const runTestTemplate = async () => {
    const to = testNumber.trim();
    if (!to || testSending) return;
    setTestSending(true);
    try {
      const r = await sendTestTemplate(to);
      if (r.error) {
        setToast(r.error);
      } else {
        setToast(
          `Confirmation ${r.confirmation?.accepted ? 'accepted ✓' : 'REJECTED ✗'}, ` +
            `ages ${r.ages?.accepted ? 'accepted ✓' : 'REJECTED ✗'}. ` +
            `Now check the phone / thread for delivered vs Failed.`,
        );
      }
    } catch {
      setToast('Test send failed — check server logs.');
    } finally {
      setTestSending(false);
    }
  };

  // Re-enable sending for order ids that were marked sent but failed (e.g. the pre-billing-fix tests).
  const [resetIds, setResetIds] = useState('1872897, 1865547, 1865545');
  const [resetting, setResetting] = useState(false);
  const runResetSent = async () => {
    const ids = resetIds.split(',').map((s) => s.trim()).filter(Boolean);
    if (!ids.length || resetting) return;
    setResetting(true);
    try {
      const r = await resetSent(ids);
      setToast(`Re-enabled ${r.reset} order(s) for sending. They'll be picked up by the backfill / auto-send.`);
    } catch {
      setToast('Reset failed — check server logs.');
    } finally {
      setResetting(false);
    }
  };

  // One-off: parse ages replies across all chats and write the notes onto the orders.
  const [agesScanning, setAgesScanning] = useState(false);
  const [agesReport, setAgesReport] = useState<AgesScanReport | null>(null);
  const runAgesScan = async (force = false) => {
    if (agesScanning) return;
    if (
      force &&
      !window.confirm(
        'Full re-scan: re-extracts ages from EVERY chat (AI call per chat) and overwrites existing notes, including any you edited by hand. Continue?',
      )
    )
      return;
    setAgesScanning(true);
    try {
      const r = await scanAges(force);
      setAgesReport(r);
      setToast(
        `Checked ${r.conversationsWithAgesRequest} chat(s)` +
          (r.skippedAlreadyNoted ? ` (${r.skippedAlreadyNoted} already noted, skipped)` : '') +
          ` — ages noted on ${r.agesNoted}. Refresh the order panel / Orders screen to see them.`,
      );
      // Drop the cached order panels so reopened chats show the fresh notes.
      setOrdersByPhone({});
      ordersLoadedRef.current.clear();
    } catch {
      setToast('Ages scan failed — check server logs.');
    } finally {
      setAgesScanning(false);
    }
  };

  const backfillReportText = (r: BackfillResult): string => {
    const lines: string[] = [];
    lines.push(`Sent confirmation (${r.sentOrders.length}):`);
    r.sentOrders.forEach((o) =>
      lines.push(`  ${o.orderId} — ${o.event} — ${o.buyer}${o.ages ? ' [+ages]' : ''}`),
    );
    if (r.noPhoneOrders.length) {
      lines.push('', `No WhatsApp number — could not message (${r.noPhoneOrders.length}):`);
      r.noPhoneOrders.forEach((o) => lines.push(`  ${o.orderId} — ${o.event} — ${o.buyer}`));
    }
    if (r.failedOrders.length) {
      lines.push('', `Failed, will retry on re-run (${r.failedOrders.length}):`);
      r.failedOrders.forEach((o) => lines.push(`  ${o.orderId} — ${o.event} — ${o.buyer}`));
    }
    if (r.unusablePhoneOrders.length) {
      lines.push('', `Invalid phone number (${r.unusablePhoneOrders.length}):`);
      r.unusablePhoneOrders.forEach((o) => lines.push(`  ${o.orderId} — ${o.event} — ${o.buyer}`));
    }
    return lines.join('\n');
  };

  return (
    <div className="h-full flex flex-col">
      {/* Top bar */}
      <div className="flex items-center gap-2 p-2 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="relative flex-1">
          <IonIcon icon={searchOutline} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search buyer, phone, event…"
            className="w-full pl-8 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm"
          />
        </div>
        <button onClick={() => setLinkModal(true)} title="Find which order a ticket link was sent to" className="p-2 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300">
          <IonIcon icon={linkOutline} />
        </button>
        <button onClick={() => setBulkComingOpen(true)} title="Tell a list of waiting buyers their tickets are on the way" className="p-2 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300">
          <IonIcon icon={timeOutline} />
        </button>
        <button onClick={() => setBulkIphoneOpen(true)} title="Ask a list of orders if anyone has an iPhone" className="p-2 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300">
          <IonIcon icon={phonePortraitOutline} />
        </button>
        <button onClick={refreshAll} title="Refresh" className="p-2 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300">
          <IonIcon icon={refreshOutline} />
        </button>
        <button onClick={() => setShowSettings((s) => !s)} title="Auto-send settings" className={'p-2 rounded-lg border ' + (showSettings ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300')}>
          <IonIcon icon={settingsOutline} />
        </button>
      </div>

      {/* Settings panel */}
      {showSettings && settings && (
        <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-blue-50/50 dark:bg-blue-900/10 text-sm space-y-3">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={settings.autoSendEnabled} onChange={(e) => saveSettings({ autoSendEnabled: e.target.checked })} />
            <span className="font-medium">Auto-send first message to new buyers</span>
          </label>
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Platforms to auto-message</div>
            <div className="flex flex-wrap gap-2">
              {PLATFORM_OPTIONS.map((p) => {
                const on = settings.enabledPlatforms.includes(p.value);
                return (
                  <button
                    key={p.value}
                    onClick={() =>
                      saveSettings({
                        enabledPlatforms: on
                          ? settings.enabledPlatforms.filter((x) => x !== p.value)
                          : [...settings.enabledPlatforms, p.value],
                      })
                    }
                    className={'px-2.5 py-1 rounded-full text-xs border ' + (on ? 'bg-green-600 text-white border-green-600' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300')}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs text-gray-500 dark:text-gray-400">Approved template</span>
            <input
              value={settings.templateName}
              onChange={(e) => setSettings({ ...settings, templateName: e.target.value })}
              onBlur={(e) => saveSettings({ templateName: e.target.value.trim() })}
              placeholder="template_name"
              className="px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm"
            />
            <input
              value={settings.templateLang}
              onChange={(e) => setSettings({ ...settings, templateLang: e.target.value })}
              onBlur={(e) => saveSettings({ templateLang: e.target.value.trim() || 'en' })}
              placeholder="en"
              className="w-16 px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm"
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            The template must be approved in Meta Business Manager. Enabling auto-send only affects orders from now on — it never messages past buyers.
          </p>

          {/* Send both templates to a test number */}
          <div className="pt-2">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              Test both templates on one number (dummy details) before messaging buyers
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <input
                value={testNumber}
                onChange={(e) => setTestNumber(e.target.value)}
                placeholder="+972 50 123 4567"
                className="px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm"
              />
              <button
                onClick={runTestTemplate}
                disabled={testSending || !testNumber.trim()}
                className="px-3 py-1.5 rounded-lg text-sm border border-blue-600 text-blue-600 dark:text-blue-400 disabled:opacity-50"
              >
                {testSending ? 'Sending…' : 'Send test (confirmation + ages)'}
              </button>
            </div>
          </div>

          {/* One-off backfill for existing orders (pick platform(s), upcoming matches) */}
          <div className="pt-2">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Platforms to backfill</div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {PLATFORM_OPTIONS.map((p) => {
                const on = backfillPlatforms.includes(p.value);
                return (
                  <button
                    key={p.value}
                    onClick={() =>
                      setBackfillPlatforms((cur) =>
                        on ? cur.filter((x) => x !== p.value) : [...cur, p.value],
                      )
                    }
                    className={
                      'text-xs px-2 py-0.5 rounded-full border ' +
                      (on
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300')
                    }
                  >
                    {on ? '✓ ' : ''}
                    {p.label}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => runBackfillLive(3)}
                disabled={backfilling || !backfillPlatforms.length}
                className="px-3 py-1.5 rounded-lg text-sm border border-gray-400 text-gray-700 dark:text-gray-300 disabled:opacity-50"
              >
                {backfilling ? 'Working…' : 'Test — send to 3'}
              </button>
              <button
                onClick={() => runBackfillLive()}
                disabled={backfilling || !backfillPlatforms.length}
                className="px-3 py-1.5 rounded-lg text-sm border border-blue-600 text-blue-600 dark:text-blue-400 disabled:opacity-50"
              >
                {backfilling ? 'Working…' : 'Backfill ticked platforms (upcoming matches)'}
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Sends the confirmation (+ ages where the rules match) to buyers on the ticked platform(s)
              for upcoming matches who were never messaged. Shows a count to confirm first; caps per run.
              Test first.
            </p>

            {/* Re-enable sending for specific order ids (e.g. ones marked sent but that failed) */}
            <div className="mt-2 flex flex-wrap gap-2 items-center">
              <input
                value={resetIds}
                onChange={(e) => setResetIds(e.target.value)}
                placeholder="order ids, comma-separated"
                className="px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm min-w-[16rem]"
              />
              <button
                onClick={runResetSent}
                disabled={resetting || !resetIds.trim()}
                className="px-3 py-1.5 rounded-lg text-sm border border-gray-400 text-gray-700 dark:text-gray-300 disabled:opacity-50"
              >
                {resetting ? 'Resetting…' : 'Re-enable sending for these order ids'}
              </button>
            </div>

            {backfillResult && (
              <div className="mt-2 p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium">
                    Last run: {backfillResult.sent} sent
                    {backfillResult.agesSent ? `, ${backfillResult.agesSent} ages` : ''}
                    {backfillResult.noPhoneOrders.length ? `, ${backfillResult.noPhoneOrders.length} no number` : ''}
                    {backfillResult.failedOrders.length ? `, ${backfillResult.failedOrders.length} failed` : ''}
                  </span>
                  <button
                    onClick={() => {
                      navigator.clipboard
                        .writeText(backfillReportText(backfillResult))
                        .then(() => setToast('Report copied'))
                        .catch(() => setToast('Copy failed'));
                    }}
                    className="text-xs px-2 py-0.5 rounded border border-gray-300 dark:border-gray-600"
                  >
                    Copy report
                  </button>
                </div>
                <pre className="text-[11px] leading-tight max-h-56 overflow-auto whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                  {backfillReportText(backfillResult)}
                </pre>
              </div>
            )}
          </div>

          {/* Auto attendee-ages rules */}
          <div className="pt-3 mt-1 border-t border-gray-200 dark:border-gray-700 space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings.agesAutoEnabled}
                onChange={(e) => saveSettings({ agesAutoEnabled: e.target.checked })}
              />
              <span className="font-medium">Auto-send attendee-ages request</span>
            </label>
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                Home teams — sent when the team is the home side (before “vs”)
              </div>
              <input
                defaultValue={(settings.agesHomeTeams || []).join(', ')}
                onBlur={(e) =>
                  saveSettings({
                    agesHomeTeams: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                  })
                }
                placeholder="Chelsea, Tottenham, Manchester City…"
                className="w-full px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm"
              />
            </div>
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                Also match anywhere in the event name (keywords)
              </div>
              <input
                defaultValue={(settings.agesEventKeywords || []).join(', ')}
                onBlur={(e) =>
                  saveSettings({
                    agesEventKeywords: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                  })
                }
                placeholder="Community Shield"
                className="w-full px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm"
              />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Matching orders get the ages template right after the confirmation. Comma-separated; case-insensitive.
            </p>

            {/* Read the buyers' ages replies and write them onto the orders */}
            <div className="pt-1">
              <div className="flex flex-wrap gap-2 items-center">
                <button
                  onClick={() => runAgesScan(false)}
                  disabled={agesScanning}
                  className="px-3 py-1.5 rounded-lg text-sm border border-blue-600 text-blue-600 dark:text-blue-400 disabled:opacity-50"
                >
                  {agesScanning ? 'Scanning…' : 'Scan chats for age replies'}
                </button>
                <button
                  onClick={() => runAgesScan(true)}
                  disabled={agesScanning}
                  className="px-2 py-1 rounded-lg text-xs border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 disabled:opacity-50"
                  title="Re-extract every chat and overwrite existing notes (one AI call per chat)"
                >
                  Full re-scan (overwrite)
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Extracts ages from replies (AI) into the orders&apos; Ages column. The normal scan skips
                chats that already have a note — cheap to re-run. New replies are picked up
                automatically as they arrive.
              </p>
              {agesReport && (
                <div className="mt-2 p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                  <div className="text-xs font-medium mb-1">
                    {agesReport.conversationsWithAgesRequest} chat(s) checked · {agesReport.agesNoted} noted
                  </div>
                  <pre className="text-[11px] leading-tight max-h-56 overflow-auto whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                    {agesReport.results
                      .map(
                        (r) =>
                          `${r.orderIds.join('/') || r.phone} — ${r.buyer || '(no name)'}: ${r.status}` +
                          (r.note ? ` → ${r.note}` : '') +
                          (r.reply ? `  [reply: ${r.reply}]` : ''),
                      )
                      .join('\n')}
                  </pre>
                </div>
              )}
            </div>
          </div>

          {/* Ticket delivery — the instructions PDF that rides the delivery template */}
          <div className="pt-3 mt-1 border-t border-gray-200 dark:border-gray-700 space-y-2">
            <div className="font-medium">Ticket delivery — entry instructions PDF</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {settings.hasInstructionsPdf
                ? `Uploaded: ${settings.instructionsFilename || 'instructions.pdf'} — attached to every delivery-gate message. Upload again to replace.`
                : 'Not uploaded yet — the instructions gate cannot send until this exists.'}
            </div>
            <input
              type="file"
              accept="application/pdf"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                try {
                  await uploadInstructionsPdf(f);
                  setSettings(await getWaSettings());
                  setToast('Instructions PDF uploaded');
                } catch (err: any) {
                  setToast(err?.response?.data?.message || 'Upload failed');
                }
                e.target.value = '';
              }}
              className="w-full text-sm text-gray-700 dark:text-gray-200"
            />
          </div>

          {/* Internal help bot */}
          <div className="pt-3 mt-1 border-t border-gray-200 dark:border-gray-700 space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!settings.helpBotEnabled}
                onChange={(e) => saveSettings({ helpBotEnabled: e.target.checked })}
              />
              <span className="font-medium">Internal help bot (staff only)</span>
            </label>
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                Staff WhatsApp numbers — their messages are answered by the AI (read-only DB queries),
                not shown in the buyer inbox
              </div>
              <input
                defaultValue={(settings.helpBotNumbers || []).join(', ')}
                onBlur={(e) =>
                  saveSettings({
                    helpBotNumbers: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                  })
                }
                placeholder="+44 7700 900123, +44 7700 900456"
                className="w-full px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm"
              />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Ask things like “sales from seller josh for arsenal chelsea”, “total profit last 7 days”,
              or “what do I owe supplier X”. Any number format works. Comma-separated.
            </p>
          </div>
        </div>
      )}

      {/* Body: list + thread */}
      <div className="flex-1 min-h-0 flex">
        {/* Conversation list */}
        <div className={'w-full sm:w-80 border-r border-gray-200 dark:border-gray-700 overflow-y-auto bg-white dark:bg-gray-800 ' + (selected ? 'hidden sm:block' : 'block')}>
          {/* Delivery-status filter chips */}
          <div className="sticky top-0 z-10 flex flex-wrap gap-1 px-2 py-1.5 border-b border-gray-100 dark:border-gray-700/60 bg-white dark:bg-gray-800">
            {([
              ['all', 'All'],
              ['failed', 'Failed'],
              ['sent', 'Sent'],
              ['delivered', 'Delivered'],
              ['read', 'Seen'],
            ] as const).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setStatusFilter(key)}
                className={
                  'text-[11px] px-2 py-0.5 rounded-full border ' +
                  (statusFilter === key
                    ? key === 'failed'
                      ? 'bg-red-600 text-white border-red-600'
                      : 'bg-blue-600 text-white border-blue-600'
                    : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300')
                }
              >
                {label} {statusCounts[key] > 0 && <span className="opacity-70">{statusCounts[key]}</span>}
              </button>
            ))}
            <span className="w-px bg-gray-200 dark:bg-gray-700 mx-0.5" />
            <button
              onClick={() => setAwaitingOnly((v) => !v)}
              className={
                'flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ' +
                (awaitingOnly
                  ? 'bg-orange-500 text-white border-orange-500'
                  : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300')
              }
              title="Show only chats awaiting the buyer's response to an alternate offer"
            >
              <IonIcon icon={hourglassOutline} />
              Awaiting offer {awaitingCount > 0 && <span className="opacity-70">{awaitingCount}</span>}
            </button>
            <button
              onClick={() => setAwaitingTicketsOnly((v) => !v)}
              className={
                'flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ' +
                (awaitingTicketsOnly
                  ? 'bg-teal-600 text-white border-teal-600'
                  : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300')
              }
              title="Tickets uploaded and held — waiting for the buyer to confirm they've read the instructions"
            >
              <IonIcon icon={ticketOutline} />
              Awaiting approval{' '}
              {awaitingTicketsCount > 0 && <span className="opacity-70">{awaitingTicketsCount}</span>}
            </button>
            <button
              onClick={() => setIssueOnly((v) => !v)}
              className={
                'flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ' +
                (issueOnly
                  ? 'bg-red-600 text-white border-red-600'
                  : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300')
              }
              title="Show only chats flagged as an issue"
            >
              <IonIcon icon={alertCircleOutline} />
              Issue {issueCount > 0 && <span className="opacity-70">{issueCount}</span>}
            </button>
          </div>
          {loading ? (
            <div className="flex justify-center py-10"><IonSpinner name="crescent" /></div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
              {convos.length === 0 ? 'No conversations yet.' : 'None match this filter.'}
            </div>
          ) : (
            filtered.map((c) => (
              <button
                key={c.phone}
                onClick={() => setSelected(c.phone)}
                className={'w-full text-left px-3 py-2.5 border-b border-gray-100 dark:border-gray-700/60 hover:bg-gray-50 dark:hover:bg-gray-700/40 ' + (selected === c.phone ? 'bg-blue-50 dark:bg-blue-900/20' : '')}
              >
                <div className="flex items-start gap-2.5">
                  <Avatar name={c.buyerName} phone={c.phone} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium truncate">{convLabel(c)}</span>
                      <span className="text-[11px] text-gray-400 shrink-0">{fmtTime(c.lastMessageAt)}</span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {(c.buyerName || c.eventName) && (
                        <span className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                          {[c.buyerName, c.eventName].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-0.5">
                      <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {c.lastMessage ? (c.lastMessage.direction === 'out' ? '↩ ' : '') + (c.lastMessage.text || `[${c.lastMessage.type}]`) : ''}
                      </span>
                      <span className="flex items-center gap-1 shrink-0">
                        {c.awaitingOfferResponse && <IonIcon icon={hourglassOutline} className="text-orange-500" title="Awaiting offer response" />}
                        {c.isIssue && <IonIcon icon={alertCircleOutline} className="text-red-600" title="Flagged as an issue" />}
                        {c.deliveryState === 'awaiting' && (
                          <IonIcon
                            icon={ticketOutline}
                            className="text-teal-600"
                            title={`Tickets held — awaiting their confirmation${c.deliveryForceAt ? ` (auto-send ${new Date(c.deliveryForceAt).toLocaleString()})` : ''}`}
                          />
                        )}
                        {c.aiMuted && <IonIcon icon={personOutline} className="text-amber-500" title="You took over" />}
                        {!c.aiMuted && <IonIcon icon={sparklesOutline} className="text-purple-500" title="AI replying" />}
                        {c.unread > 0 && <span className="bg-green-600 text-white text-[10px] rounded-full px-1.5 py-0.5">{c.unread}</span>}
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>

        {/* Thread */}
        <div className={'flex-1 min-w-0 flex flex-col bg-gray-50 dark:bg-gray-900 ' + (selected ? 'flex' : 'hidden sm:flex')}>
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-sm text-gray-400">Select a conversation</div>
          ) : !thread || thread.phone !== selected ? (
            <div className="flex-1 flex items-center justify-center gap-2 text-sm text-gray-400">
              <IonSpinner name="crescent" style={{ width: 18, height: 18 }} /> Loading conversation…
            </div>
          ) : (
            <>
              {/* Thread header */}
              <div className="flex items-center gap-2 p-2.5 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                <button className="sm:hidden p-1" onClick={() => setSelected(null)}><IonIcon icon={arrowBackOutline} /></button>
                <Avatar name={thread.buyerName} phone={thread.phone} size={38} />
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{convLabel(thread)}</div>
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1.5 min-w-0">
                    {/* Phone never truncates and copies on tap — it was disappearing on mobile,
                        buried mid-string in one truncating line. */}
                    {thread.phone && (
                      <button
                        onClick={() => copyPhone(thread.phone)}
                        className="shrink-0 flex items-center gap-0.5 font-mono text-blue-600 dark:text-blue-300 active:opacity-60"
                        title="Copy phone number"
                      >
                        <IonIcon icon={copyOutline} style={{ fontSize: 11 }} />
                        +{thread.phone}
                      </button>
                    )}
                    <span className="truncate">{[thread.buyerName, thread.eventName].filter(Boolean).join(' · ')}</span>
                  </div>
                </div>
                <button
                  onClick={() => setOrderMinimized((m) => !m)}
                  className={'flex items-center gap-1 px-2 py-1 rounded-lg text-xs border ' + (!orderMinimized ? 'bg-gray-700 text-white border-gray-700' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300')}
                  title={orderMinimized ? 'Show order details' : 'Hide order details'}
                >
                  {loadingOrders ? <IonSpinner name="crescent" style={{ width: 14, height: 14 }} /> : <IonIcon icon={documentTextOutline} />}
                  Order
                </button>
                <button
                  onClick={askAges}
                  disabled={askingAges}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs border border-blue-300 text-blue-600 dark:text-blue-300 disabled:opacity-50"
                  title="Send the buyer the approved 'ages of attendees' template"
                >
                  {askingAges ? <IonSpinner name="crescent" style={{ width: 14, height: 14 }} /> : <IonIcon icon={peopleOutline} />}
                  Ages
                </button>
                <button
                  onClick={askIphone}
                  disabled={askingIphone}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs border border-blue-300 text-blue-600 dark:text-blue-300 disabled:opacity-50"
                  title="Send the buyer the approved 'does at least one person have an iPhone?' template (Yes/No)"
                >
                  {askingIphone ? <IonSpinner name="crescent" style={{ width: 14, height: 14 }} /> : <IonIcon icon={phonePortraitOutline} />}
                  iPhone?
                </button>
                <button
                  onClick={askClub}
                  disabled={askingClub}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs border border-blue-300 text-blue-600 dark:text-blue-300 disabled:opacity-50"
                  title="Send the buyer the approved 'which club do you support?' template (names both teams, or Neutral)"
                >
                  {askingClub ? <IonSpinner name="crescent" style={{ width: 14, height: 14 }} /> : <IonIcon icon={flagOutline} />}
                  Club?
                </button>
                <button
                  onClick={chaseOffer}
                  disabled={chasingOffer}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs border border-amber-300 text-amber-700 dark:text-amber-300 disabled:opacity-50"
                  title="Chase an unanswered seat offer — sends the approved 'offer_follow_up' template with the same Yes/No buttons"
                >
                  {chasingOffer ? <IonSpinner name="crescent" style={{ width: 14, height: 14 }} /> : <IonIcon icon={timeOutline} />}
                  Chase offer
                </button>
                <button
                  onClick={() => setDeliverOpen(true)}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs border border-teal-300 text-teal-600 dark:text-teal-300"
                  title="Send this buyer their tickets — held behind the read-the-instructions gate, or sent straight away"
                >
                  <IonIcon icon={ticketOutline} />
                  Tickets
                </button>
                {thread.pendingDelivery && (
                  <span
                    className={
                      'flex items-center gap-1 px-2 py-1 rounded-lg text-xs border ' +
                      (thread.pendingDelivery.state === 'awaiting'
                        ? 'bg-amber-100 dark:bg-amber-900/30 border-amber-400 text-amber-700 dark:text-amber-300'
                        : 'bg-emerald-100 dark:bg-emerald-900/30 border-emerald-400 text-emerald-700 dark:text-emerald-300')
                    }
                    title={
                      thread.pendingDelivery.state === 'awaiting'
                        ? `Tickets held until they tap "I've read them" — auto-sends ${thread.pendingDelivery.forceAt ? new Date(thread.pendingDelivery.forceAt).toLocaleString() : 'at the deadline'}`
                        : `Tickets ${thread.pendingDelivery.state === 'forced' ? 'auto-sent at the deadline (no confirmation)' : 'delivered'}`
                    }
                  >
                    {thread.pendingDelivery.state === 'awaiting'
                      ? '🎟 held'
                      : thread.pendingDelivery.state === 'forced'
                      ? '🎟 auto-sent'
                      : '🎟 delivered'}
                    {thread.pendingDelivery.state === 'awaiting' && (
                      <button onClick={sendHeldNow} className="underline font-semibold" title="Skip the gate and send the held tickets now">
                        send now
                      </button>
                    )}
                  </span>
                )}
                <button
                  onClick={openOfferComposer}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs border border-emerald-300 text-emerald-600 dark:text-emerald-300"
                  title="Offer this buyer different seats (better category/block, or a swap) — goes as a template, so it works outside the 24-hour window"
                >
                  <IonIcon icon={swapHorizontalOutline} />
                  Offer
                </button>
                <button
                  onClick={toggleMute}
                  className={'flex items-center gap-1 px-2 py-1 rounded-lg text-xs border ' + (thread.aiMuted ? 'bg-amber-100 dark:bg-amber-900/30 border-amber-400 text-amber-700 dark:text-amber-300' : 'border-purple-300 text-purple-600 dark:text-purple-300')}
                  title={thread.aiMuted ? 'AI is muted — you are handling this' : 'AI is auto-replying — click to take over'}
                >
                  <IonIcon icon={thread.aiMuted ? personOutline : sparklesOutline} />
                  {thread.aiMuted ? 'You' : 'AI'}
                </button>
                <button
                  onClick={toggleAwaitingOffer}
                  className={'flex items-center gap-1 px-2 py-1 rounded-lg text-xs border ' + (thread.awaitingOfferResponse ? 'bg-orange-500 text-white border-orange-500' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300')}
                  title={thread.awaitingOfferResponse ? 'Waiting on the buyer\'s response to an alternate offer — click to clear' : 'Mark: waiting on the buyer\'s response to an alternate offer (different seats/split etc)'}
                >
                  <IonIcon icon={hourglassOutline} />
                  {thread.awaitingOfferResponse ? 'Awaiting reply' : 'Mark offer sent'}
                </button>
                <button
                  onClick={toggleIssue}
                  className={'flex items-center gap-1 px-2 py-1 rounded-lg text-xs border ' + (thread.isIssue ? 'bg-red-600 text-white border-red-600' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300')}
                  title={thread.isIssue ? 'Flagged as an issue — click to clear' : 'Flag this chat as an issue'}
                >
                  <IonIcon icon={alertCircleOutline} />
                  {thread.isIssue ? 'Issue' : 'Flag issue'}
                </button>
              </div>

              {/* Order details panel (open by default; cached per chat; collapsible) */}
              {selected && ordersByPhone[selected] !== undefined && !orderMinimized && (
                <div className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 max-h-64 overflow-y-auto">
                  <div className="sticky top-0 flex items-center justify-between px-3 py-1 bg-gray-50 dark:bg-gray-900/60 border-b border-gray-100 dark:border-gray-700/50">
                    <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Order details</span>
                    <button
                      onClick={() => setOrderMinimized(true)}
                      className="text-[11px] px-1.5 rounded text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
                      title="Minimize"
                    >
                      – minimize
                    </button>
                  </div>
                  {ordersByPhone[selected].length === 0 ? (
                    <div className="p-3 text-xs text-gray-500 dark:text-gray-400">No linked order found for this chat.</div>
                  ) : (
                    ordersByPhone[selected].map((o) => (
                      <div key={o.orderId} className="p-3 text-xs border-b border-gray-100 dark:border-gray-700/50 last:border-0">
                        <div className="font-semibold mb-1.5">
                          Order {o.orderId}
                          {o.ref ? <span className="text-gray-500 dark:text-gray-400"> · ref {o.ref}</span> : null}
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                          <Field label="Match" value={o.event} />
                          <Field label="Category" value={o.category} />
                          <Field label="Quantity" value={o.quantity} />
                          <Field label="Platform" value={o.platform} />
                          <Field label="Match date" value={o.eventDate} />
                          <Field label="Order date" value={o.orderDate} />
                          <Field
                            label="Total"
                            value={
                              o.totalPriceGBP != null
                                ? `£${Number(o.totalPriceGBP).toFixed(2)}` +
                                  (o.currency && o.currency !== 'GBP' && o.totalNative != null
                                    ? ` (${o.currency === 'USD' ? '$' : o.currency === 'EUR' ? '€' : o.currency + ' '}${Number(o.totalNative).toFixed(2)})`
                                    : '')
                                : null
                            }
                          />
                          <Field label="Bought from" value={o.boughtFrom} />
                          <Field label="Price bought" value={o.priceBought != null ? `£${Number(o.priceBought).toFixed(2)}` : null} />
                          <Field label="Profit" value={o.profit != null ? `£${Number(o.profit).toFixed(2)}` : null} />
                        </div>
                        {o.agesNote ? (
                          <div className="mt-1.5"><span className="text-gray-500 dark:text-gray-400">Ages:</span> <span className="font-medium">{o.agesNote}</span></div>
                        ) : null}
                        {o.remarks ? (
                          <div className="mt-1.5"><span className="text-gray-500 dark:text-gray-400">Remarks:</span> {o.remarks}</div>
                        ) : null}
                        {o.notes ? (
                          <div className="mt-0.5"><span className="text-gray-500 dark:text-gray-400">Notes:</span> {o.notes}</div>
                        ) : null}
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {thread.messages.map((m, i) => (
                  <div key={i} className={'flex ' + (m.direction === 'out' ? 'justify-end' : 'justify-start')}>
                    <div className={'max-w-[78%] rounded-2xl px-3 py-1.5 text-sm ' + (m.direction === 'out' ? 'bg-green-600 text-white rounded-br-sm' : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-bl-sm')}>
                      {m.type === 'template' && <div className="text-[10px] opacity-70 mb-0.5">Template · {m.templateName}</div>}
                      {m.byAi && <div className="text-[10px] opacity-80 mb-0.5 flex items-center gap-0.5"><IonIcon icon={sparklesOutline} /> AI</div>}
                      {m.reactionEmoji ? (
                        <div className="whitespace-pre-wrap break-words">
                          <span className="text-xl mr-1">{m.reactionEmoji}</span>
                          <span className="opacity-70 text-xs">reacted</span>
                        </div>
                      ) : m.mediaId || ['image', 'sticker', 'audio', 'video', 'document'].includes(m.type) ? (
                        <div className="space-y-1">
                          <Attachment wamid={m.wamid} type={m.type} mimeType={m.mimeType} filename={m.filename} />
                          {(m.caption || (m.text && !/^\[.*\]$/.test(m.text))) && (
                            <div className="whitespace-pre-wrap break-words">{m.caption || m.text}</div>
                          )}
                        </div>
                      ) : (
                        <div className="whitespace-pre-wrap break-words">{m.text || `[${m.type}]`}</div>
                      )}
                      <div className={'text-[10px] mt-0.5 flex items-center gap-1 justify-end ' + (m.direction === 'out' ? 'text-white/70' : 'text-gray-400')}>
                        {fmtTime(m.at)}
                        {m.direction === 'out' && <OutStatus status={m.status} />}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>

              {/* Composer */}
              {thread.withinWindow ? (
                <ReplyComposer
                  ref={composerRef}
                  chatPhone={thread.phone}
                  sendingPhoto={sendingPhoto}
                  onSend={send}
                  onSendPhoto={sendPhoto}
                />
              ) : (
                <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-amber-50 dark:bg-amber-900/15 text-xs text-amber-700 dark:text-amber-300 text-center">
                  Outside the 24-hour reply window. WhatsApp only allows an approved template to re-open this conversation — free-form replies are blocked until the buyer messages again.
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Ticket delivery. Default holds the tickets behind the read-the-instructions gate:
          instructions template now, tickets auto-released on their tap — or force-sent at the
          deadline (night before the match, or +30min when sent on match day). */}
      {deliverOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setDeliverOpen(false)}>
          <div
            className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-900 shadow-xl p-4 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="font-semibold text-gray-900 dark:text-gray-100">Send tickets</div>

            {threadOrderIds.length > 1 && (
              <label className="block text-xs">
                <span className="text-amber-700 dark:text-amber-300">
                  This number has {threadOrderIds.length} orders — which one are these tickets for?
                </span>
                <select
                  value={deliverOrderId}
                  onChange={(e) => setDeliverOrderId(e.target.value)}
                  className="mt-1 w-full px-2 py-1.5 rounded-lg border border-amber-300 dark:border-amber-700 bg-white dark:bg-gray-800 text-sm"
                >
                  <option value="">Pick an order…</option>
                  {threadOrderIds.map((id) => {
                    const o = (ordersByPhone[selected || ''] || []).find((x) => String(x.orderId) === id);
                    return (
                      <option key={id} value={id}>
                        {id}{o ? ` — ${o.event || ''}${o.quantity ? ` × ${o.quantity}` : ''}` : ''}
                      </option>
                    );
                  })}
                </select>
              </label>
            )}

            <label className="block text-xs">
              <span className="text-gray-500 dark:text-gray-400">Ticket links — one per line</span>
              <textarea
                value={deliverLinks}
                onChange={(e) => setDeliverLinks(e.target.value)}
                rows={4}
                placeholder={'https://...\nhttps://...'}
                className="mt-0.5 w-full px-2 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-950 text-sm text-gray-900 dark:text-gray-100 font-mono"
              />
              <span className={parsedDeliverLinks.length ? 'text-teal-600 dark:text-teal-300 font-medium' : 'text-gray-400'}>
                {parsedDeliverLinks.length
                  ? `${parsedDeliverLinks.length} link${parsedDeliverLinks.length === 1 ? '' : 's'} detected${
                      parsedDeliverLinks.length === 1 ? ' (long links wrap — still one link)' : ''
                    }`
                  : deliverLinks.trim()
                  ? 'No links recognised — links must start with http(s)://'
                  : ''}
              </span>
            </label>

            <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={deliverOneTime}
                onChange={(e) => setDeliverOneTime(e.target.checked)}
              />
              <span>
                <b>One-time links</b> — buyer gets a lock warning right before the links (opens once,
                matchday phone only, add to Wallet immediately)
              </span>
            </label>

            <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={deliverReplacement}
                onChange={(e) => setDeliverReplacement(e.target.checked)}
              />
              <span>
                <b>Replacement tickets</b> — the tickets sent earlier were cancelled by the club. The buyer
                gets the cancellation notice (same seats as before, use only the new tickets) and the new
                tickets go out straight after it, whichever button you press — no gate, nothing to approve.
              </span>
            </label>

            <div className="space-y-2">
              {deliverPairs.map((pair, i) => (
                <div key={i} className="rounded-lg border border-gray-200 dark:border-gray-700 p-2 space-y-1.5">
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span className="font-medium">Ticket {i + 1} — iPhone + Android pair</span>
                    <button
                      type="button"
                      onClick={() => setDeliverPairs((ps) => ps.filter((_, j) => j !== i))}
                      className="text-red-400 hover:text-red-500"
                    >
                      remove
                    </button>
                  </div>
                  <input
                    value={pair.iphone}
                    onChange={(e) => setDeliverPairs((ps) => ps.map((p, j) => (j === i ? { ...p, iphone: e.target.value } : p)))}
                    placeholder="📱 iPhone (Apple Wallet) link"
                    className="w-full px-2 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-950 text-sm text-gray-900 dark:text-gray-100 font-mono"
                  />
                  <input
                    value={pair.android}
                    onChange={(e) => setDeliverPairs((ps) => ps.map((p, j) => (j === i ? { ...p, android: e.target.value } : p)))}
                    placeholder="🤖 Android (Google Wallet) link"
                    className="w-full px-2 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-950 text-sm text-gray-900 dark:text-gray-100 font-mono"
                  />
                </div>
              ))}
              <button
                type="button"
                onClick={() => setDeliverPairs((ps) => [...ps, { iphone: '', android: '' }])}
                className="text-xs text-teal-600 dark:text-teal-300 font-medium"
              >
                + Add iPhone/Android pair (same ticket, two wallet links)
              </button>
            </div>

            <label className="block text-xs">
              <span className="text-gray-500 dark:text-gray-400">Ticket PDFs</span>
              <input
                type="file"
                accept="application/pdf"
                multiple
                onChange={(e) => setDeliverFiles(Array.from(e.target.files || []))}
                className="mt-0.5 w-full text-sm text-gray-700 dark:text-gray-200"
              />
              {deliverFiles.length > 0 && (
                <span className="text-gray-500 dark:text-gray-400">{deliverFiles.length} PDF(s) selected</span>
              )}
            </label>

            {!settings?.hasInstructionsPdf && (
              <p className="text-[11px] text-amber-600 dark:text-amber-400">
                No instructions PDF uploaded yet — the gate can't send without it. Add it in Settings,
                or use "Just send now".
              </p>
            )}
            <p className="text-[11px] text-gray-500 dark:text-gray-400">
              <b>With instructions gate</b> sends only the instructions and holds the tickets until the
              buyer taps that they've read them — then they send automatically (and auto-send anyway the
              night before the match, or 30 minutes from now on match day).{' '}
              <b>Instructions + tickets</b> sends both straight away, nothing held.{' '}
              <b>Just send now</b> sends the tickets only.
            </p>

            <div className="flex gap-2 justify-end pt-1">
              <button
                onClick={() => setDeliverOpen(false)}
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => submitDelivery('both')}
                disabled={deliverSending || (parsedDeliverLinks.length === 0 && deliverFiles.length === 0 && !deliverPairs.some((p) => p.iphone.trim() || p.android.trim())) || !settings?.hasInstructionsPdf}
                className="px-3 py-2 rounded-lg border border-teal-500 text-teal-600 dark:text-teal-300 text-sm font-medium disabled:opacity-50"
                title="Send the instructions AND the tickets straight away — nothing held, no waiting on the buyer"
              >
                Instructions + tickets
              </button>
              <button
                onClick={() => submitDelivery('now')}
                disabled={deliverSending || (parsedDeliverLinks.length === 0 && deliverFiles.length === 0 && !deliverPairs.some((p) => p.iphone.trim() || p.android.trim()))}
                className="px-3 py-2 rounded-lg border border-teal-500 text-teal-600 dark:text-teal-300 text-sm font-medium disabled:opacity-50"
                title="Skip the gate — send the tickets immediately"
              >
                Just send now
              </button>
              <button
                onClick={() => submitDelivery('gate')}
                disabled={deliverSending || (parsedDeliverLinks.length === 0 && deliverFiles.length === 0 && !deliverPairs.some((p) => p.iphone.trim() || p.android.trim())) || !settings?.hasInstructionsPdf}
                className="px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium disabled:opacity-50"
              >
                {deliverSending ? 'Sending…' : 'With instructions gate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Seat-offer composer: its own component so typing in it re-renders the modal, not the whole screen. */}
      {offerOpen && (
        <OfferComposer
          initial={offerInitial}
          sending={offerSending}
          onCancel={() => setOfferOpen(false)}
          onSend={sendOffer}
        />
      )}

      {linkModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-20" onClick={() => setLinkModal(false)}>
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-[92%] max-w-2xl max-h-[70vh] overflow-y-auto p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <IonIcon icon={linkOutline} /> Find a ticket link
              </h2>
              <button className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300" onClick={() => setLinkModal(false)}>Close</button>
            </div>
            <div className="flex gap-2 mb-3">
              <input
                value={linkQuery}
                onChange={(e) => setLinkQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') doLinkSearch(); }}
                placeholder="Paste a link or its tail (e.g. the barcode segment)…"
                className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 text-sm font-mono"
              />
              <button onClick={doLinkSearch} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium">Search</button>
            </div>
            {linkSearching ? (
              <div className="py-6 text-center"><IonSpinner name="crescent" /></div>
            ) : linkHits === null ? (
              <div className="text-sm text-slate-500 py-6 text-center">Search the full record of every ticket link the system has sent or uploaded.</div>
            ) : linkHits.length === 0 ? (
              <div className="text-sm text-slate-500 py-6 text-center">No match — this link was not sent or uploaded by the system.</div>
            ) : (
              <div className="space-y-2">
                <div className="text-xs text-slate-500">{linkHits.length} match{linkHits.length === 1 ? '' : 'es'}</div>
                {linkHits.map((h, i) => (
                  <div key={i} className="border border-slate-200 dark:border-slate-700 rounded-lg p-2.5 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-gray-900 dark:text-gray-100">
                        Order {h.orderId || '—'} · {h.buyerName || 'unknown buyer'}
                      </span>
                      <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">{h.via}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {h.eventName || ''}{h.phone ? ` · +${h.phone}` : ''}{h.wallet && h.wallet !== 'universal' ? ` · ${h.wallet}` : ''}
                      {h.at ? ` · ${new Date(h.at).toLocaleString()}` : ''}
                    </div>
                    <div className="text-[11px] font-mono text-slate-400 break-all mt-1">{h.link}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {bulkIphoneOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-20" onClick={() => setBulkIphoneOpen(false)}>
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-[92%] max-w-2xl max-h-[80vh] overflow-y-auto p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <IonIcon icon={phonePortraitOutline} /> Ask “anyone have an iPhone?” in bulk
              </h2>
              <button className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300" onClick={() => setBulkIphoneOpen(false)}>Close</button>
            </div>
            <p className="text-xs text-slate-500 mb-2">
              Paste order ids (one per line, or separated by commas/spaces). Each buyer gets the approved
              <span className="font-mono"> attendee_has_iphone</span> template with Yes/No buttons.
            </p>
            <textarea
              value={bulkIphoneIds}
              onChange={(e) => setBulkIphoneIds(e.target.value)}
              placeholder={'1886959\n1886657\n1886639'}
              rows={7}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 text-sm font-mono"
            />
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-slate-500">{parsedBulkIds.length} id{parsedBulkIds.length === 1 ? '' : 's'} detected</span>
              <button
                onClick={runBulkIphone}
                disabled={bulkIphoneSending || parsedBulkIds.length === 0}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {bulkIphoneSending ? <IonSpinner name="crescent" style={{ width: 16, height: 16 }} /> : <IonIcon icon={phonePortraitOutline} />}
                Send to all
              </button>
            </div>
            {bulkIphoneResult && (
              <div className="mt-4 space-y-2">
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {bulkIphoneResult.sent} sent · {bulkIphoneResult.failed} failed · {bulkIphoneResult.total} total
                </div>
                <div className="space-y-1">
                  {bulkIphoneResult.results.map((r) => (
                    <div key={r.id} className="flex items-center gap-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5">
                      <span className={'text-xs px-1.5 py-0.5 rounded-full ' + (r.ok ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300')}>
                        {r.ok ? 'sent' : 'failed'}
                      </span>
                      <span className="font-mono text-xs text-slate-500">{r.id}</span>
                      <span className="text-gray-800 dark:text-gray-200 truncate">
                        {r.ok ? `${r.buyer || 'buyer'}${r.event ? ' · ' + r.event : ''}` : (r.error || 'unknown error')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {bulkComingOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-20" onClick={() => setBulkComingOpen(false)}>
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-[92%] max-w-2xl max-h-[80vh] overflow-y-auto p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <IonIcon icon={timeOutline} /> Tell waiting buyers their tickets are coming
              </h2>
              <button className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300" onClick={() => setBulkComingOpen(false)}>Close</button>
            </div>
            <p className="text-xs text-slate-500 mb-2">
              Paste order ids (one per line, or separated by commas/spaces). Each buyer gets the approved
              <span className="font-mono"> tickets_on_the_way</span> template: their order is confirmed, tickets are
              being prepared and will arrive in this chat in good time. Nothing is delivered and no hold is armed.
            </p>
            <textarea
              value={bulkComingIds}
              onChange={(e) => setBulkComingIds(e.target.value)}
              placeholder={'1886959\n1886657\n1886639'}
              rows={7}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 text-sm font-mono"
            />
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-slate-500">{parsedComingIds.length} id{parsedComingIds.length === 1 ? '' : 's'} detected</span>
              <button
                onClick={runBulkComing}
                disabled={bulkComingSending || parsedComingIds.length === 0}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {bulkComingSending ? <IonSpinner name="crescent" style={{ width: 16, height: 16 }} /> : <IonIcon icon={timeOutline} />}
                Send to all
              </button>
            </div>
            {bulkComingResult && (
              <div className="mt-4 space-y-2">
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {bulkComingResult.sent} sent · {bulkComingResult.failed} failed · {bulkComingResult.total} total
                </div>
                <div className="space-y-1">
                  {bulkComingResult.results.map((r) => (
                    <div key={r.id} className="flex items-center gap-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5">
                      <span className={'text-xs px-1.5 py-0.5 rounded-full ' + (r.ok ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300')}>
                        {r.ok ? 'sent' : 'failed'}
                      </span>
                      <span className="font-mono text-xs text-slate-500">{r.id}</span>
                      <span className="text-gray-800 dark:text-gray-200 truncate">
                        {r.ok ? `${r.buyer || 'buyer'}${r.event ? ' · ' + r.event : ''}` : (r.error || 'unknown error')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <IonToast isOpen={!!toast} message={toast || ''} duration={3500} onDidDismiss={() => setToast(null)} color="danger" />
    </div>
  );
};

export default MessagesContent;
